import { Box, Hidden, Typography } from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFlag } from '@unleash/proxy-client-react'
import { useMatch } from 'react-router-dom'
import useBucket from '../../hooks/useBucket'
import useSelectedAlbums from '../../hooks/useSelectedAlbums'
import useSelectedMedia from '../../hooks/useSelectedMedia'
import useWebview from '../../hooks/useWebview'
import InviteAvatars from '../inviteAvatars'
import DeselectButton from './DeselectButton'
import IconDropDownMenu from './IconDropDownMenu'
import IconsMenu from './IconsMenu'
import LogoMenu from './LogoMenu'
import SearchBar from './SearchBar'
import SelectButton from './SelectButton'
import SettingsMenu from './SettingsMenu'

interface Props {
  searchBar?: boolean
  displayDrawerToggle?: boolean
  iconMenu?: boolean
  showSelectButton?: boolean
  settingsMenu?: boolean
  inviteAndSharingAvatars?: boolean
}

const APPBAR_HEIGHT = 68

function BucketTitle({ bucketTitle }: { bucketTitle: string | undefined }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 0, marginRight: 3 }}>
      <Typography
        variant="h6"
        noWrap
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: { xs: '1rem', md: '1.25rem' },
          fontWeight: 700,
          color: 'black',
          textDecoration: 'none',
          maxWidth: { xs: '100px', sm: '250px', md: '100%' }
        }}
      >
        {bucketTitle}
      </Typography>
    </Box>
  )
}

export default function Appbar({
  searchBar = true,
  displayDrawerToggle = true,
  iconMenu = true,
  showSelectButton = true,
  settingsMenu = true,
  inviteAndSharingAvatars = false
}: Readonly<Props>) {
  const theme = useTheme()
  const useUpdatedAlbumActions = useFlag('useUpdatedAlbumActions')
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'))
  const { selectedModeEnabled: selectedModeEnabledAlbums, selectedAlbums } = useSelectedAlbums()
  const { selectedModeEnabled: selectedModeEnabledMedia } = useSelectedMedia()
  const selectedModeEnabled = selectedModeEnabledAlbums || selectedModeEnabledMedia
  const path = useMatch('/bucket/:bucketId/:slug')
  const isTrash = path?.params.slug === 'trash'
  const isSharing = path?.params.slug === 'sharing'
  const isAlbums = path?.params.slug === 'albums'
  const { bucket } = useBucket()
  const { isWebview } = useWebview()

  if (isWebview) {
    return <Box sx={{ height: APPBAR_HEIGHT }} />
  }

  return (
    <MuiAppBar
      position="static"
      elevation={0}
      color="inherit"
      sx={{ borderBottom: '1px solid #dfdfdf', color: theme.palette.primary.main, alignItems: 'center' }}
    >
      <Toolbar
        disableGutters={isTablet}
        sx={{
          padding: { xs: '8px' },
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: '100%'
        }}
      >
        <Box sx={{ display: 'flex', flex: 1 }}>
          {(useUpdatedAlbumActions || !selectedModeEnabled) && <LogoMenu displayDrawerToggle={displayDrawerToggle} />}
          {!isAlbums && isMobile && useUpdatedAlbumActions && <BucketTitle bucketTitle={bucket?.title} />}
          {!useUpdatedAlbumActions && selectedModeEnabled && <DeselectButton />}
          <Hidden lgDown>{searchBar && !isSharing && <SearchBar />}</Hidden>
        </Box>
        {selectedAlbums.size >= 1 && useUpdatedAlbumActions && isAlbums && <IconsMenu />}
        {!useUpdatedAlbumActions && showSelectButton && <SelectButton />}
        {useUpdatedAlbumActions && isAlbums && selectedModeEnabled && !isSharing && (
          <div style={{ marginRight: 8 }}>
            <DeselectButton />
          </div>
        )}
        {!useUpdatedAlbumActions && iconMenu && !isTrash && !isSharing && <IconsMenu />}
        {!useUpdatedAlbumActions && iconMenu && !isTrash && !isSharing && <IconDropDownMenu />}
        {!isAlbums && !isMobile && useUpdatedAlbumActions && <BucketTitle bucketTitle={bucket?.title} />}
        {bucket && useUpdatedAlbumActions && inviteAndSharingAvatars && !isTrash && <InviteAvatars bucket={bucket} />}
        {settingsMenu && <SettingsMenu />}
      </Toolbar>
    </MuiAppBar>
  )
}
